// Considered: ArcStageInspectorMolecule — that is the stage editor body, a different selection
// kind with a different field set (key date, requirements, terminal choice); this is the
// transition editor the inspector rail opens for `selection.kind === 'transition'`, with its own
// target, trigger-kind choice and per-kind fields (auto anchor, manual comment). See spec Reuse
// Audit (arc-editor-canvas-v1.md).
import React from 'react';
import { Trans, useTranslation } from 'localization';
import type { Button } from 'primereact/button';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import GlyphAtom from '../../atoms/Glyph/GlyphAtom';
import ChoiceCardGroupAtom from '../../atoms/ChoiceCardGroup/ChoiceCardGroupAtom';
import TypeFieldRowMolecule from '../studio/TypeFieldRowMolecule';
import InlineConfirmMolecule from '../studio/InlineConfirmMolecule';
import { focusPrimeButton } from '../../../utils/focusPrimeButton';
import UnifiedFilterTreeMolecule from '../UnifiedFilterTree/UnifiedFilterTreeMolecule';
import type { FilterGroup } from 'types/filters';
import type { PropertyOption } from '../PropertyFilter/PropertyFilterMolecule';
import './ArcTransitionInspectorMolecule.scss';

export type ArcTransitionKind = 'manual' | 'auto' | 'calculated';
export type ArcTransitionAnchorSource = 'keyDate' | 'property';

export interface ArcTransitionInspectorSelectOption {
  value: string;
  label: string;
}

export interface ArcTransitionInspectorMoleculeProps {
  /** Plain-language read-back of the transition (`acTransSentence`). */
  summary: string;
  /** The stage this transition leaves — its name is read into the remove confirm; the stage
   * itself is the selection, not a field (a stage owns exactly one exit transition). */
  fromStageName: string;
  toStageId: string;
  onToStageChange: (value: string) => void;
  stageOptions: ArcTransitionInspectorSelectOption[];
  label: string;
  onLabelChange: (value: string) => void;
  kind: ArcTransitionKind;
  onKindChange: (kind: ArcTransitionKind) => void;
  anchorSource: ArcTransitionAnchorSource;
  onAnchorSourceChange: (source: ArcTransitionAnchorSource) => void;
  /** `''` — no anchor picked. */
  anchor: string;
  onAnchorChange: (value: string) => void;
  keyDateOptions: ArcTransitionInspectorSelectOption[];
  sphereDateOptions: ArcTransitionInspectorSelectOption[];
  offset: string;
  onOffsetChange: (value: string) => void;
  offsetSupported: boolean;
  condition?: FilterGroup;
  onConditionChange: (group: FilterGroup) => void;
  conditionAvailableProperties: PropertyOption[];
  requireComment: boolean;
  onRequireCommentChange: (checked: boolean) => void;
  editable: boolean;
  onApply: () => void;
  /** Called only after the inline confirm is accepted. */
  onRemove: () => void;
  /** Whether Remove takes only this path and leaves the stage its other exits
   * (`transitionRemoveKeepsOtherExits`); the confirm states that effect instead of "no way
   * forward". Defaults to the whole exit going. */
  removeKeepsOtherExits?: boolean;
  className?: string;
}

/** The transition editor the inspector rail's `selection.body` hosts for
 * `selection.kind === 'transition'` (`arc-inspector.jsx:264-371`, `TransitionEditor`). */
const ArcTransitionInspectorMolecule: React.FC<ArcTransitionInspectorMoleculeProps> = ({
  summary,
  fromStageName,
  toStageId,
  onToStageChange,
  stageOptions,
  label,
  onLabelChange,
  kind,
  onKindChange,
  anchorSource,
  onAnchorSourceChange,
  anchor,
  onAnchorChange,
  keyDateOptions,
  sphereDateOptions,
  offset,
  onOffsetChange,
  offsetSupported,
  condition,
  onConditionChange,
  conditionAvailableProperties,
  requireComment,
  onRequireCommentChange,
  editable,
  onApply,
  onRemove,
  removeKeepsOtherExits = false,
  className = '',
}) => {
  const { t } = useTranslation();
  const ns = 'studio.arcEditor.inspector.transition';
  // Remove is consequential (the spec's reversal table) — the inline confirm states the effect in
  // place; "Keep" returns focus to the button that opened it.
  const [confirmingRemove, setConfirmingRemove] = React.useState(false);
  const removeButtonRef = React.useRef<Button>(null);
  const keepTransition = () => {
    setConfirmingRemove(false);
    focusPrimeButton(removeButtonRef.current);
  };

  return (
    <div className={`arc-transition-inspector ${className}`.trim()} data-datum="side.panel#3" data-kind="panel">
      <div className="arc-transition-inspector__body">
        <p className="arc-transition-inspector__sentence" data-datum="side.actranssentence-x-stages" data-kind="callout">
          {summary}
        </p>
        <TypeFieldRowMolecule
          label={t(`${ns}.toLabel`)}
          layout="stacked"
          mode={editable ? 'edit' : 'disabled'}
          datum="side.to"
          controls={[
            {
              kind: 'select',
              value: toStageId,
              options: stageOptions,
              onChange: onToStageChange,
              ariaLabel: t(`${ns}.toLabel`),
              datumKind: 'select',
            },
          ]}
        />
        <TypeFieldRowMolecule
          label={t(`${ns}.labelLabel`)}
          hint={t(`${ns}.labelHint`)}
          layout="stacked"
          mode={editable ? 'edit' : 'disabled'}
          kind="field"
          datum="side.children#6"
          labelDatum="side.label#12"
          controls={[
            {
              kind: 'text',
              value: label,
              placeholder: t(`${ns}.labelPlaceholder`),
              onChange: onLabelChange,
              ariaLabel: t(`${ns}.labelLabel`),
              datum: 'side.e-g-start-investigating',
              datumKind: 'input',
            },
          ]}
        />

        <section className="arc-transition-inspector__section" data-container="side > panel#3 > group > group:children">
          <span className="arc-transition-inspector__section-label" data-datum="side.label#13" data-kind="heading">
            {t(`${ns}.triggersHeading`)}
          </span>
          <ChoiceCardGroupAtom
            ariaLabel={t(`${ns}.triggersHeading`)}
            value={kind}
            onChange={(value) => onKindChange(value as ArcTransitionKind)}
            disabled={!editable}
            options={[
              {
                value: 'manual',
                label: t(`${ns}.kindManualLabel`),
                note: t(`${ns}.kindManualNote`),
                icon: 'lucide:hand',
                datum: 'side.button#2',
                datumKind: 'button',
              },
              {
                value: 'auto',
                label: t(`${ns}.kindAutoLabel`),
                note: t(`${ns}.kindAutoNote`),
                icon: 'lucide:calendar-clock',
                datum: 'side.button#2',
                datumKind: 'button',
              },
              {
                value: 'calculated',
                label: t(`${ns}.kindCalculatedLabel`),
                note: t(`${ns}.kindCalculatedNote`),
                icon: 'lucide:sigma',
                datum: 'side.button#2',
                datumKind: 'button',
              },
            ]}
          />
        </section>

        {kind === 'auto' && (
          <section className="arc-transition-inspector__section" data-container="side > panel#3 > group > group:children">
            <span className="arc-transition-inspector__section-label" data-datum="side.label#14" data-kind="heading">
              {t(`${ns}.whichDateHeading`)}
            </span>
            <ChoiceCardGroupAtom
              ariaLabel={t(`${ns}.whichDateHeading`)}
              value={anchorSource}
              onChange={(value) => onAnchorSourceChange(value as ArcTransitionAnchorSource)}
              disabled={!editable}
              options={[
                {
                  value: 'keyDate',
                  label: t(`${ns}.anchorKeyDateLabel`),
                  note: t(`${ns}.anchorKeyDateNote`),
                  icon: 'lucide:calendar-days',
                  datum: 'side.button#3',
                  datumKind: 'button',
                },
                {
                  value: 'property',
                  label: t(`${ns}.anchorPropertyLabel`),
                  note: t(`${ns}.anchorPropertyNote`),
                  icon: 'lucide:database',
                  datum: 'side.button#3',
                  datumKind: 'button',
                },
              ]}
            />
            {anchorSource === 'keyDate' ? (
              <TypeFieldRowMolecule
                label={t(`${ns}.keyDateLabel`)}
                layout="stacked"
                mode={editable ? 'edit' : 'disabled'}
                datum="side.key-date#2"
                controls={[
                  {
                    kind: 'select',
                    value: anchor,
                    options: [{ value: '', label: t(`${ns}.keyDateNone`) }, ...keyDateOptions],
                    onChange: onAnchorChange,
                    ariaLabel: t(`${ns}.keyDateLabel`),
                    datumKind: 'select',
                  },
                ]}
              />
            ) : (
              <TypeFieldRowMolecule
                label={t(`${ns}.spherePropertyLabel`)}
                hint={t(`${ns}.spherePropertyHint`)}
                layout="stacked"
                mode={editable ? 'edit' : 'disabled'}
                datum="side.date-on-the-sphere"
                controls={[
                  {
                    kind: 'select',
                    value: anchor,
                    options: [{ value: '', label: t(`${ns}.spherePropertyNone`) }, ...sphereDateOptions],
                    onChange: onAnchorChange,
                    ariaLabel: t(`${ns}.spherePropertyLabel`),
                    datumKind: 'select',
                  },
                ]}
              />
            )}
            <TypeFieldRowMolecule
              label={t(`${ns}.offsetLabel`)}
              layout="stacked"
              mode={editable ? 'edit' : 'disabled'}
              kind="field"
              datum="side.children#7"
              labelDatum="side.label#15"
              errorDatum="side.the-engine-parses#2"
              errorMessage={
                offsetSupported ? undefined : (
                  <Trans
                    i18nKey={`${ns}.offsetInvalid`}
                    values={{ value: offset }}
                    components={{ whole: <strong data-datum="side.whole-days#2" data-kind="strong" />, b: <strong /> }}
                  />
                )
              }
              controls={[
                {
                  kind: 'text',
                  value: offset,
                  placeholder: t(`${ns}.offsetPlaceholder`),
                  onChange: onOffsetChange,
                  ariaLabel: t(`${ns}.offsetLabel`),
                  datum: 'side.e-g-1d#2',
                  datumKind: 'input',
                },
              ]}
            />
            {!anchor && (
              <p className="arc-transition-inspector__note arc-transition-inspector__note--warn" data-datum="side.a-date-triggered-transition-with-no-date" data-kind="note">
                <GlyphAtom glyph="lucide:alert-triangle" aria-hidden />
                {t(`${ns}.noAnchorNote`)}
              </p>
            )}
            {anchor && anchorSource === 'property' && (
              <p className="arc-transition-inspector__note" data-datum="side.nothing-has-to-answer-this-one-the-date-" data-kind="note">
                {t(`${ns}.propertyAnchoredNote`)}
              </p>
            )}
          </section>
        )}

        {kind === 'calculated' && (
          <section className="arc-transition-inspector__section" data-container="side > panel#3 > group > group:children">
            <span className="arc-transition-inspector__section-label" data-datum="side.label#16" data-kind="heading">
              {t(`${ns}.conditionHeading`)}
            </span>
            <UnifiedFilterTreeMolecule
              value={condition}
              onChange={onConditionChange}
              availableProperties={conditionAvailableProperties}
              framed={false}
              readsAs
              subject={t(`${ns}.conditionSubject`)}
              datumScope="side"
              cardDatumId="side.label#17"
              readOnly={!editable}
            />
          </section>
        )}

        {kind === 'manual' && (
          <section className="arc-transition-inspector__section" data-container="side > panel#3 > group > group:children">
            <span className="arc-transition-inspector__section-label" data-datum="side.label#18" data-kind="heading">
              {t(`${ns}.whoMayMoveHeading`)}
            </span>
            <p className="arc-transition-inspector__note" data-datum="side.anyone-who-can-see-the-sphere-may-move-i" data-kind="note">
              <Trans
                i18nKey={`${ns}.anyoneMayMoveNote`}
                components={{ once: <strong data-datum="side.once-the-stage-s-requirements-are-met" data-kind="strong" /> }}
              />
            </p>
            <label className="arc-transition-inspector__check" data-datum="side.ask-for-a-comment" data-kind="checkbox">
              <input
                type="checkbox"
                checked={requireComment}
                disabled={!editable}
                data-datum="side.ask-for-a-comment"
                data-kind="checkbox"
                onChange={(event) => onRequireCommentChange(event.target.checked)}
              />
              <span>
                {t(`${ns}.requireCommentLabel`)}
                <i>{t(`${ns}.requireCommentHint`)}</i>
              </span>
            </label>
          </section>
        )}
      </div>
      {editable && confirmingRemove && (
        <InlineConfirmMolecule
          tone="danger"
          testId="arc-transition-inspector-remove-confirm"
          titleId="arc-transition-inspector-remove-confirm-title"
          title={t(`${ns}.removeConfirm.${removeKeepsOtherExits ? 'titleOnePath' : 'title'}`, { from: fromStageName })}
          body={t(`${ns}.removeConfirm.${removeKeepsOtherExits ? 'bodyOnePath' : 'body'}`, { from: fromStageName })}
          actions={[
            {
              label: t(`${ns}.removeConfirm.${removeKeepsOtherExits ? 'acceptOnePath' : 'accept'}`),
              onClick: onRemove,
              variant: 'danger',
              'data-testid': 'arc-transition-inspector-remove-confirm-accept',
            },
            {
              label: t(`${ns}.removeConfirm.keep`),
              onClick: keepTransition,
              variant: 'text',
              'data-testid': 'arc-transition-inspector-remove-confirm-dismiss',
            },
          ]}
        />
      )}
      {editable && (
        <div className="arc-transition-inspector__actions" data-container="side > actions">
          <ButtonAtom variant="secondary" size="small" onClick={onApply} data-datum="side.apply#2" data-kind="button">
            {t(`${ns}.apply`)}
          </ButtonAtom>
          <ButtonAtom
            variant="danger"
            size="small"
            icon={<GlyphAtom glyph="lucide:trash-2" aria-hidden />}
            ref={removeButtonRef}
            onClick={() => setConfirmingRemove(true)}
            data-datum="side.remove"
            data-kind="button"
          >
            {t(`${ns}.remove`)}
          </ButtonAtom>
        </div>
      )}
    </div>
  );
};

ArcTransitionInspectorMolecule.displayName = 'ArcTransitionInspectorMolecule';

export default ArcTransitionInspectorMolecule;
